import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import getOr from 'lodash/fp/getOr';
import map from 'lodash/fp/map';
import toString from 'lodash/fp/toString';
import toLower from 'lodash/fp/toLower';

export default function getDefaultBookingInfo({ booking, recoCookie }) {
	// Validate that booking exists
	if (!booking) {
		return {
			booking_flow: ['shopping cart'],
			room_finding_method_array: ['eCAW'],
			flex_count: '0',
			booking_arrival_date: '',
			booking_departure_date: '',
			booking_currency: '',
			booking_number_rooms: 0,
			room_nights_array: [],
			room_adults_array: [],
			room_children_array: [],
			room_code_array: [],
			reco_room_code_array: [],
			room_rate_array: [],
			room_property_array: [],
			room_name_array: [],
			room_type_array: [],
			room_package_code_array: [],
			reco_room_package_code_array: [],
			room_package_ows_code_array: [],
			room_package_name_array: [],
			booking_taxes: '0.00',
			booking_fees: '',
			room_offer_array: [''],
		};
	}

	let propertyAvailable = Array.isArray(booking?.hotelProducts) && booking.hotelProducts.length > 0;

	// This variables tells us the booking engine the visitor is using to complete an itinerary or booking
	// Suggested values include "shopping cart" and "reservations.fourseasons.com"; more values may be required in the future.
	// For multi- room bookings, this value should be repeated within the array so that the number of values equals the number of rooms in the booking.
	// Example: booking_flow=["shopping cart"];
	let booking_flow = ['shopping cart'];

	// This array tells us how someone arrived at the booking engine in the first place.  Currently, the only way to get here is the eCAW so set that
	let room_finding_method_array = ['eCAW'];

	// PART OF AVAILABILITY CHECK - This describes the number of flex dates a user has selected in an availability search.
	const flex_count = '0';

	let booking_arrival_date = '';
	let booking_departure_date = '';

	// The booking_currency variable specifies what currency you are using to send revenue values to Tealium.
	// For FourSeasons.com, the default currency is USD; the booking_currency only needs to be specified on pages where the currency being sent is in a currency other than the default (USD).
	let booking_currency = '';

	// room_nights_array - An array of positive integers, reflecting the number of nights being checked for each room included in the availability check.
	let room_nights_array = [];

	// An array of positive integers, reflecting the number of adults staying in each room included in the booking.
	let room_adults_array = [];

	// An array of positive integers, reflecting the number of children staying in each room included in the booking.
	let room_children_array = [];

	//  PART OF AVAILABILITY CHECK - This array provides the property code of each hotel for which availability is being checked.
	// Amesh is checking availability for 3 rooms in Austin:  "room_property_array":["aus","aus","aus"]
	let room_property_array = [];

	// The room_code_array provides a three-character code for each room type that is part of the booking.
	let reco_room_code_array = [];

	// The room_code_array provides a three-character code for each room type that is part of the booking.
	let room_code_array = [];

	// The room_rate_array tells us the average daily rate for each room included in the booking.
	let room_rate_array = [];

	// The room_package_code_array provides an alphanumeric code for each package type that is part of the booking. e.g - room_package_code_array":["FSHBAR"]
	let room_package_code_array = [];

	// The room_package_code_array provides an alphanumeric code for each package type that is part of the booking. e.g - room_package_code_array":["FSHBAR"]
	let reco_room_package_code_array = [];

	// The room_package_ows_code_array provides an alphanumeric code for each package type that is part of the booking. e.g - room_package_ows_code_array":["RACBAR"]
	let room_package_ows_code_array = [];

	// The room_package_name_array array provides a user-friendly name for the package type(s) that are part of the booking.  "room_package_name_array":["Room Rate"]
	let room_package_name_array = [];

	// The room_name_array array provides a user-friendly name for the room type(s) that are part of the booking.  "room_name_array":["One-Bedroom Suite"]
	let room_name_array = [];

	// The room_type_array array provides a way to understand whether booked rooms are private retreats or hotel rooms for the room type(s) that are part of the booking. "room_type_array":["private retreat", "hotel"]
	let room_type_array = [];

	// Sum of all taxes being charged for the booking (Needs to be string) "booking_taxes": "24.39" (NOTE: not available from endpoint for UK destinations)
	// DEV NOTE: Will contain `total fees and taxes` as agreed with Tealium
	let booking_taxes;
	// String containing amount of fees included in the booking, with no currency symbol, two decimal places and a period decimal delimiter.
	// DEV NOTE: As taxes and fees are provided to SHC as one figure, advised to leave booking_fees empty and populate `booking_taxes` only.
	let booking_fees;

	// The room_offer_array provides additional details about any special promotional offers associated with the booking.
	// Typically these will be for “exclusive offers” or “open offers” that are available to customers who register or log in as part of their booking as an additional incentive to register.
	//Example one room booking -  "room_offer_array":["florence/italy_promotion"]
	// Example when only 2nd room in booking has an associated offer -  "room_offer_array":["","HawaiiCo_opCampaign"]
	//DEV NOTE: Advised to leave as array with Empty string - "room_offer_array":[""]
	let room_offer_array = [''];

	// return booking
	return flow(
		get(['hotelProducts']),
		map(hotelProduct => {
			const recoItem =
				recoCookie?.datalakeRecoIds?.find(
					x =>
						x?.ors?.toLowerCase() ===
							flow(getOr('', ['orsHotelCode']), toLower)(hotelProduct).toLowerCase() &&
						x.type === 'property'
				) ?? null;
			const isReco = !!recoItem;
			booking_arrival_date = [get(['checkInDate'])(hotelProduct)];
			booking_departure_date = [get(['checkOutDate'])(hotelProduct)];
			// TODO: Needs to be in USD currencyCode (Single API may be including this update to make USD available SHC-233)
			booking_currency = get(['price', 'total', 'cash', 'usdCurrencyCode'])(hotelProduct);
			room_nights_array.push(flow(get('duration'), toString)(hotelProduct));
			room_adults_array.push(flow(get(['guestCount', 'numberAdults']), toString)(hotelProduct));
			room_children_array.push(flow(get(['guestCount', 'numberChildren']), toString)(hotelProduct));

			const taxesFeesChargesTotal = flow(
				get(['price', 'prepay', 'taxesFeesChargesTotal', 'cash', 'usdAmount']),
				val => val && toString(val)
			)(hotelProduct);
			booking_taxes = taxesFeesChargesTotal ?? '0.00';
			booking_fees = taxesFeesChargesTotal ?? '';

			flow(
				get(['roomTypes']),
				map(room => {
					reco_room_code_array.push(isReco ? room.roomTypeId : 'na');
					room_code_array.push(room.roomTypeId);
				})
			)(hotelProduct);

			flow(
				get(['roomTypes']),
				map(room => room_name_array.push(room.name))
			)(hotelProduct);

			room_type_array.push(
				flow(getOr(false, ['roomRate', 'isPrivateRetreat']), isPrivateRetreat =>
					isPrivateRetreat ? 'private retreat' : 'hotel'
				)(hotelProduct)
			);

			room_property_array.push(flow(getOr('', ['orsHotelCode']), toLower)(hotelProduct));

			room_rate_array.push(
				flow(
					getOr('', ['roomRate', 'averageNightlyPriceExcludingTaxesFees', 'cash', 'usdAmount']),
					toString
				)(hotelProduct)
			);

			let packageCode = get(['roomRate', 'orsRatePlanCode'])(hotelProduct);
			const packageOwsCode = get(['roomRate', 'ratePlanCode'])(hotelProduct);
			if (!packageCode && packageOwsCode) {
				packageCode = packageOwsCode.match(/^[a-zA-Z]+$/) ? 'corp' : 'group';
			}
			room_package_code_array.push(packageCode);
			reco_room_package_code_array.push(isReco ? packageCode : 'na');
			room_package_ows_code_array.push(packageOwsCode);
			room_package_name_array.push(get(['roomRate', 'ratePlanTitle'])(hotelProduct));
		}),
		() => ({
			booking_flow,
			room_finding_method_array,
			flex_count,
			booking_arrival_date,
			booking_departure_date,
			booking_currency,
			booking_number_rooms: booking.hotelProducts?.length || 0,
			room_nights_array,
			room_adults_array,
			room_children_array,
			...(propertyAvailable
				? {
						reco_room_code_array,
						reco_room_package_code_array,
					}
				: {}),
			room_code_array,
			room_rate_array,
			room_property_array,
			room_name_array,
			room_type_array,
			room_package_code_array,
			room_package_ows_code_array,
			room_package_name_array,
			booking_taxes,
			booking_fees,
			room_offer_array,
		})
	)(booking);
}
